import { useEffect } from 'react';

/**
 * Publishes the visual viewport to CSS, once, for the whole app.
 *
 * iOS Safari does not shrink the layout when the keyboard opens. innerHeight,
 * 100vh and 100dvh all stay at the full window, the keyboard slides over the
 * bottom of the page, and Safari pans the view to keep the focused field in
 * sight. Anything sized to the window or pinned to its bottom ends up either
 * under the keyboard or floating mid-screen above dead space.
 * visualViewport is the only thing that reports the area actually visible.
 *
 * Written to <html> as custom properties rather than React state, so a
 * keyboard animation or an emoji-panel switch restyles the page without
 * re-rendering it:
 *
 *   --vv-height          visible height, px
 *   --vv-top             how far Safari has panned the visible area down, px
 *   --vv-bottom          how far the visible area's bottom edge sits above
 *                        the window's, px: the keyboard's height when it slides
 *                        over the page, 0 when it resizes the window or is shut
 *   data-keyboard-open   present while the keyboard (or emoji panel) is up, or
 *                        while a text field has focus on a touch device
 *
 * The classes in index.css under "Keyboard-aware screens" are the consumers.
 * On desktop the visual viewport is the window, so the values match what 100dvh
 * would give and nothing moves.
 *
 * After each write it dispatches `vvchange` on window. Anything that measures
 * layout against these values listens for that rather than for
 * visualViewport directly: React runs child effects before parent effects, so
 * a child's own visualViewport listener would fire before this one and
 * measure the page before it had been resized.
 */

/** Smaller differences are browser chrome (the URL bar collapsing), not a keyboard. */
const KEYBOARD_MIN_PX = 120;

/** Input types that raise no keyboard: pickers, toggles and buttons. */
const NO_KEYBOARD_TYPES = new Set([
  'checkbox', 'radio', 'range', 'color', 'file', 'button', 'submit', 'reset', 'image', 'hidden',
  'date', 'datetime-local', 'month', 'week', 'time',
]);

/** Whether the focused element is one the on-screen keyboard types into. */
function isTypingTarget(el: Element | null): boolean {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !el.readOnly && !el.disabled && !NO_KEYBOARD_TYPES.has(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

export const VV_CHANGE_EVENT = 'vvchange';

export function useVisualViewportVars() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const coarse = window.matchMedia('(pointer: coarse)');
    let frame = 0;
    let lastHeight = vv.height;

    const write = () => {
      frame = 0;
      /* A pinch zoom also shrinks the visual viewport. Following it would
         reflow the page under someone who zoomed to read it, so while zoomed
         the page keeps its full-window layout. */
      const zoomed = Math.abs(vv.scale - 1) > 0.01;
      const height = zoomed ? window.innerHeight : vv.height;
      const top = zoomed ? 0 : vv.offsetTop;
      /* Two signals, because iOS does not behave one way. In Safari the
         keyboard slides over a full-height window, so the visual viewport
         comes up short of innerHeight. But where the window itself shrinks
         with the keyboard, innerHeight and the visual viewport shrink
         together, the difference stays near zero, and that test alone never
         fires. That is the likeliest reason the tab bar stayed up over the
         keyboard on the conversation list, though it was not confirmed on a
         device. So a touch device with a text field focused also counts. On a
         phone a focused text field is a raised keyboard, and on desktop the
         pointer is fine, so this never fires there. */
      const shortOfWindow = window.innerHeight - vv.height > KEYBOARD_MIN_PX;
      const typing = coarse.matches && isTypingTarget(document.activeElement);
      const keyboardOpen = !zoomed && (shortOfWindow || typing);
      root.style.setProperty('--vv-height', `${height}px`);
      root.style.setProperty('--vv-top', `${top}px`);
      root.style.setProperty('--vv-bottom', `${zoomed ? 0 : Math.max(0, window.innerHeight - vv.offsetTop - vv.height)}px`);
      root.toggleAttribute('data-keyboard-open', keyboardOpen);
      window.dispatchEvent(new Event(VV_CHANGE_EVENT));

      /* Keep the focused field in sight when the visible height changes.
         iOS scrolls a focused field into view when it takes focus, which is
         before the keyboard has finished opening and before the shell has
         shrunk to it. A field low in a form, the password under the email,
         can be scrolled to a spot the shrinking shell then covers. So once
         the new height has been applied, the field is scrolled to the nearest
         edge that shows it, which does nothing if it is already visible.
         Height changes only: a pan fires scroll events too, and following
         those would fight someone scrolling the form while typing. */
      const heightChanged = Math.abs(height - lastHeight) > 1;
      lastHeight = height;
      const focused = document.activeElement;
      if (keyboardOpen && heightChanged && isTypingTarget(focused)) {
        requestAnimationFrame(() => (focused as HTMLElement).scrollIntoView({ block: 'nearest' }));
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(write);
    };

    write();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    // Focus moves before the keyboard animates and before any resize, so the
    // flag follows the field directly rather than waiting on a height change.
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      root.style.removeProperty('--vv-height');
      root.style.removeProperty('--vv-top');
      root.style.removeProperty('--vv-bottom');
      root.removeAttribute('data-keyboard-open');
    };
  }, []);
}
